<template>
    <v-dialog v-model="dialogVisible" max-width="900px">
        <v-card>
            <v-card-title>
                <span class="headline">
                    {{
                        isEdited
                            ? $t("licence_web.edit_licence")
                            : $t("licence_web.add_licence")
                    }}
                </span>
            </v-card-title>

            <v-card-text>
                <v-container>
                    <v-form ref="form" v-model="valid" lazy-validation>
                        <v-row>
                            <v-col cols="12" class="mb-4">
                                <v-btn-toggle
                                    v-model="activeLang"
                                    mandatory
                                    dense
                                >
                                    <!-- Base fields -->
                                    <v-btn
                                        value="base"
                                        small
                                        :class="
                                            activeLang === 'base'
                                                ? 'primary white--text'
                                                : ''
                                        "
                                    >
                                        {{
                                            $t("licence_web.general") ||
                                            "Obecné"
                                        }}
                                    </v-btn>

                                    <!-- CZ -->
                                    <v-btn
                                        value="cz"
                                        small
                                        :class="
                                            activeLang === 'cz'
                                                ? 'primary white--text'
                                                : ''
                                        "
                                    >
                                        CZ
                                    </v-btn>

                                    <!-- Other languages -->
                                    <v-btn
                                        v-for="lang in filteredLanguages"
                                        :key="lang.code"
                                        :value="lang.code"
                                        small
                                        :class="
                                            activeLang === lang.code
                                                ? 'primary white--text'
                                                : ''
                                        "
                                    >
                                        {{ lang.name }} ({{ lang.code }})
                                    </v-btn>
                                </v-btn-toggle>
                            </v-col>

                            <!-- BASE TAB -->
                            <template v-if="activeLang === 'base'">
                                <v-col cols="4">
                                    <v-text-field
                                        v-model="formData.icon_tag"
                                        :label="$t('licence_web.icon_tag')"
                                        :counter="100"
                                    ></v-text-field>
                                </v-col>

                                <v-col cols="12" class="d-flex">
                                    <v-switch
                                        v-model="formData.is_popular"
                                        :label="$t('licence_web.is_popular')"
                                        color="orange"
                                        class="mr-6"
                                    ></v-switch>

                                    <v-switch
                                        v-model="formData.is_active"
                                        :label="$t('general.active')"
                                        color="green"
                                    ></v-switch>
                                </v-col>
                            </template>

                            <!-- CZ TAB -->
                            <template v-if="activeLang === 'cz'">
                                <v-col cols="12" class="py-0">
                                    <v-text-field
                                        v-model="formData.title"
                                        :label="$t('licence_web.title_cz')"
                                        :rules="titleRules"
                                        required
                                    ></v-text-field>
                                </v-col>

                                <v-col cols="12" class="py-0">
                                    <v-text-field
                                        v-model="formData.subtitle"
                                        :label="$t('licence_web.subtitle_cz')"
                                    ></v-text-field>
                                </v-col>

                                <v-col cols="12" class="py-0">
                                    <label class="mb-2 d-block">
                                        {{ $t("licence_web.description_cz") }}
                                    </label>

                                    <div class="d-flex mb-1" style="gap: 4px">
                                        <v-btn
                                            small
                                            icon
                                            @click="undoCz"
                                            class="itb-custom-btn"
                                        >
                                            <v-icon small>mdi-undo</v-icon>
                                        </v-btn>
                                        <v-btn
                                            small
                                            icon
                                            :color="
                                                showCodeCz
                                                    ? 'primary'
                                                    : undefined
                                            "
                                            @click="toggleCodeCz"
                                            class="itb-custom-btn"
                                        >
                                            <v-icon small>mdi-code-tags</v-icon>
                                        </v-btn>
                                    </div>

                                    <vue-editor
                                        v-if="!showCodeCz"
                                        ref="czEditor"
                                        v-model="formData.description"
                                        :editorToolbar="customToolbar"
                                    />
                                    <v-textarea
                                        v-else
                                        v-model="formData.description"
                                        rows="8"
                                        auto-grow
                                        outlined
                                    ></v-textarea>

                                    <div
                                        v-if="!formData.description"
                                        class="error--text text-caption mt-1"
                                    >
                                        {{
                                            $t(
                                                "licence_web.description_required"
                                            )
                                        }}
                                    </div>
                                </v-col>

                                <v-col cols="12" class="py-0 mt-5">
                                    <v-text-field
                                        v-model="formData.benefits"
                                        :label="$t('licence_web.benefits_cz')"
                                        :placeholder="
                                            $t(
                                                'licence_web.benefits_placeholder'
                                            )
                                        "
                                    ></v-text-field>
                                </v-col>
                            </template>

                            <!-- Other languages tab -->
                            <template v-else-if="activeLang !== 'base'">
                                <div
                                    v-for="lang in filteredLanguages"
                                    v-if="activeLang === lang.code"
                                    :key="`lang-${lang.code}`"
                                    cols="12"
                                >
                                    <v-col cols="12" class="py-0">
                                        <v-text-field
                                            v-model="
                                                formData[`title_${lang.code}`]
                                            "
                                            :label="
                                                $t('licence_web.title_lang') +
                                                ' ' +
                                                lang.name +
                                                ' (' +
                                                lang.code +
                                                ')'
                                            "
                                        ></v-text-field
                                    ></v-col>
                                    <v-col cols="12" class="py-0">
                                        <v-text-field
                                            class="mt-2"
                                            v-model="
                                                formData[
                                                    `subtitle_${lang.code}`
                                                ]
                                            "
                                            :label="
                                                $t(
                                                    'licence_web.subtitle_lang'
                                                ) +
                                                ' ' +
                                                lang.name +
                                                ' (' +
                                                lang.code +
                                                ')'
                                            "
                                        ></v-text-field
                                    ></v-col>
                                    <v-col cols="12" class="py-0">
                                        <label class="mt-2 d-block">
                                            {{
                                                $t(
                                                    "licence_web.description_lang"
                                                ) +
                                                " " +
                                                lang.name +
                                                " (" +
                                                lang.code +
                                                ")"
                                            }}
                                        </label>

                                        <div
                                            class="d-flex mb-1"
                                            style="gap: 4px"
                                        >
                                            <v-btn
                                                small
                                                icon
                                                @click="undoLang(lang.code)"
                                                class="itb-custom-btn"
                                            >
                                                <v-icon small>mdi-undo</v-icon>
                                            </v-btn>
                                            <v-btn
                                                small
                                                icon
                                                :color="
                                                    showCode[lang.code]
                                                        ? 'primary'
                                                        : undefined
                                                "
                                                @click="
                                                    toggleCodeLang(lang.code)
                                                "
                                                class="itb-custom-btn"
                                            >
                                                <v-icon small
                                                    >mdi-code-tags</v-icon
                                                >
                                            </v-btn>
                                        </div>

                                        <vue-editor
                                            v-if="!showCode[lang.code]"
                                            :ref="`editor_${lang.code}`"
                                            class="mt-2"
                                            v-model="
                                                formData[
                                                    `description_${lang.code}`
                                                ]
                                            "
                                            :editorToolbar="customToolbar" />
                                        <v-textarea
                                            v-else
                                            class="mt-2"
                                            v-model="
                                                formData[
                                                    `description_${lang.code}`
                                                ]
                                            "
                                            rows="8"
                                            auto-grow
                                            outlined
                                        ></v-textarea
                                    ></v-col>
                                    <v-col cols="12" class="py-0 mt-5">
                                        <v-text-field
                                            class="mt-2"
                                            v-model="
                                                formData[
                                                    `benefits_${lang.code}`
                                                ]
                                            "
                                            :label="
                                                $t(
                                                    'licence_web.benefits_lang'
                                                ) +
                                                ' ' +
                                                lang.name +
                                                ' (' +
                                                lang.code +
                                                ')'
                                            "
                                            :placeholder="
                                                $t(
                                                    'licence_web.benefits_placeholder'
                                                )
                                            "
                                        ></v-text-field>
                                    </v-col>
                                </div>
                            </template>
                        </v-row>
                    </v-form>
                </v-container>
            </v-card-text>

            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="blue darken-1" text @click="closeDialog">
                    {{ $t("general.cancel") }}
                </v-btn>
                <v-btn
                    color="blue darken-1"
                    text
                    @click="saveLicence"
                    :disabled="!valid"
                >
                    {{ $t("general.save") }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { VueEditor } from "vue2-editor";

export default {
    name: "LicenceWebForm",

    props: {
        licence: {
            type: Object,
            required: true,
        },
        languages: {
            type: Array,
            required: true,
        },
        visible: {
            type: Boolean,
            default: false,
        },
        isEdited: {
            type: Boolean,
            default: false,
        },
    },

    components: {
        VueEditor,
    },

    data() {
        return {
            valid: true,
            activeLang: "base",
            formData: {
                id: null,
                title: "",
                subtitle: "",
                description: "",
                icon_tag: "",
                benefits: "",
                is_popular: 0,
                is_active: 0,
            },
            showCodeCz: false,
            showCode: {},
            customToolbar: [
                ["bold", "italic"],
                [{ header: [3, 4, false] }],
                [{ list: "bullet" }],
            ],
            titleRules: [(v) => !!v || this.$t("licence_web.title_required")],
            descriptionRules: [
                (v) => !!v || this.$t("licence_web.description_required"),
            ],
        };
    },

    computed: {
        filteredLanguages() {
            return this.languages.filter((l) => l.code !== "cz");
        },
        dialogVisible: {
            get() {
                return this.visible;
            },
            set(value) {
                if (!value) {
                    this.$emit("close");
                }
            },
        },
    },

    watch: {
        visible(val) {
            if (val) {
                this.activeLang = "base";

                if (this.isEdited) {
                    this.formData = JSON.parse(JSON.stringify(this.licence));
                    this.ensureDynamicFields();
                } else {
                    this.resetForm();
                }
            }
        },
        languages() {
            this.ensureDynamicFields();
        },
    },

    methods: {
        toggleCodeCz() {
            this.showCodeCz = !this.showCodeCz;
        },
        undoCz() {
            const editor = this.$refs.czEditor;
            if (editor && editor.quill && editor.quill.history) {
                editor.quill.history.undo();
            }
        },
        toggleCodeLang(code) {
            if (this.showCode[code] === undefined) {
                this.$set(this.showCode, code, false);
            }
            this.showCode[code] = !this.showCode[code];
        },
        undoLang(code) {
            const refName = `editor_${code}`;
            let editor = this.$refs[refName];

            if (Array.isArray(editor)) {
                editor = editor[0];
            }

            if (editor && editor.quill && editor.quill.history) {
                editor.quill.history.undo();
            }
        },

        ensureDynamicFields() {
            const data = { ...this.formData };

            this.filteredLanguages.forEach((lang) => {
                const tField = `title_${lang.code}`;
                const sField = `subtitle_${lang.code}`;
                const dField = `description_${lang.code}`;
                const bField = `benefits_${lang.code}`;

                if (data[tField] === undefined) data[tField] = "";
                if (data[sField] === undefined) data[sField] = "";
                if (data[dField] === undefined) data[dField] = "";
                if (data[bField] === undefined) data[bField] = "";

                if (this.showCode[lang.code] === undefined) {
                    this.$set(this.showCode, lang.code, false);
                }
            });

            if (data.is_active === undefined || data.is_active === null) {
                data.is_active = 0;
            }
            if (data.is_popular === undefined || data.is_popular === null) {
                data.is_popular = 0;
            }

            this.formData = data;
        },

        resetForm() {
            const base = {
                id: null,
                title: "",
                subtitle: "",
                description: "",
                icon_tag: "",
                benefits: "",
                is_popular: 0,
                is_active: 0,
            };

            this.languages.forEach((lang) => {
                if (lang.code === "cz") return;
                base[`title_${lang.code}`] = "";
                base[`subtitle_${lang.code}`] = "";
                base[`description_${lang.code}`] = "";
                base[`benefits_${lang.code}`] = "";
                this.$set(this.showCode, lang.code, false);
            });

            this.formData = base;

            if (this.$refs.form) {
                this.$refs.form.resetValidation();
            }
            this.showCodeCz = false;
        },

        closeDialog() {
            this.$emit("close");
            this.resetForm();
        },

        async saveLicence() {
            if (this.$refs.form.validate()) {
                try {
                    if (this.isEdited) {
                        await this.updateLicence();
                    } else {
                        await this.createLicence();
                    }
                    this.closeDialog();
                } catch (error) {
                    console.error(error.response?.data || error);
                    this.$emit("snackMessage", "general.error", "error");
                }
            }
        },

        async createLicence() {
            const response = await axios.post("/licences-web", this.formData, {
                headers: {
                    Authorization:
                        "Bearer " +
                        this.$store.state.tokenData.user.access_token,
                },
            });

            const licence = response.data.licence || response.data;
            this.$emit("addLicence", licence);
            this.$emit(
                "snackMessage",
                "licence_web.created_successfully",
                "success"
            );
        },

        async updateLicence() {
            const response = await axios.put(
                `/licences-web/${this.licence.id}`,
                this.formData,
                {
                    headers: {
                        Authorization:
                            "Bearer " +
                            this.$store.state.tokenData.user.access_token,
                    },
                }
            );

            const licence = response.data.licence || response.data;
            this.$emit("updateLicence", licence);
            this.$emit(
                "snackMessage",
                "licence_web.updated_successfully",
                "success"
            );
        },
    },
};
</script>

<style scoped>
.itb-custom-btn {
    background: #1976d2;
    color: white !important;
    border-radius: 4px;
    padding: 4px 8px;
    border: none;
    cursor: pointer;
    font-weight: bold;
    margin-right: 4px;
}

.itb-custom-btn i {
    color: white !important;
}

.itb-custom-btn:hover {
    background: #1565c0;
}
</style>
